import React, { useEffect, useState, useRef } from 'react';
import { CheckIcon, ChevronDownIcon, BuildingIcon } from 'lucide-react';
import { useCompany } from '../../app/providers/CompanyProvider';
export function HeaderCompanySelector() {
  const {
    selectedCompany,
    companies,
    setSelectedCompany
  } = useCompany();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  // Close on Escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    }
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);
  const selectCompany = (company: typeof selectedCompany) => {
    setSelectedCompany(company);
    setIsOpen(false);
  };
  return <div className="relative" ref={dropdownRef} data-id="element-15224" data-mp="3166hisuoh4i3">
      <button onClick={() => setIsOpen(!isOpen)} aria-label={`Selected company: ${selectedCompany.name}`} aria-expanded={isOpen} aria-haspopup="listbox" className="flex items-center justify-between w-full sm:min-w-48 sm:w-auto max-w-[160px] sm:max-w-64 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors" data-id="element-15225" data-mp="316g9glza9lqf">
        <div className="flex items-center min-w-0 flex-1" data-id="element-15226" data-mp="1prpj7wydtiw">
          {selectedCompany.logo ? <img src={selectedCompany.logo} alt="" className="w-4 h-4 rounded-full mr-2 flex-shrink-0" data-id="element-15227" data-mp="3ttk6a3o82tr4" /> : <BuildingIcon size={14} className="mr-2 text-gray-500 flex-shrink-0" data-id="element-15228" data-mp="2viize4v0qlbg" />}
          <span className="truncate text-xs" data-id="element-15229" data-mp="30j6zrh550eic">{selectedCompany.name}</span>
        </div>
        <ChevronDownIcon size={14} className={`ml-2 flex-shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-15230" data-mp="34jqtsbuez209" />
      </button>

      {isOpen && <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} data-id="element-15231" data-mp="2ct2w59ry8erc" />

          <div role="listbox" aria-label="Select company or sub merchant" className="absolute left-0 z-20 w-[280px] sm:w-80 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden" data-id="element-15232" data-mp="2gsbn66vltyj7">
            {/* Master merchant section */}
            <div className="border-b border-gray-200" data-id="element-15233" data-mp="1t2r4zq79z5az">
              <div className="px-3 py-2 bg-gray-50" data-id="element-15234" data-mp="1lkjbceqqch9z">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide" data-id="element-15235" data-mp="1ljzznfroap4e">
                  Master Merchant
                </span>
              </div>
              {companies.filter(c => c.type === 'master').map(company => {
            const isSelected = selectedCompany.id === company.id;
            return <div key={company.id} role="option" aria-selected={isSelected} onClick={() => selectCompany(company)} className={`flex items-center px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`} data-id="element-15236" data-mp="12ni1afibjqz6">
                      <div className="flex items-center flex-1 min-w-0" data-id="element-15237" data-mp="jue4il9t0y0c">
                        {company.logo ? <img src={company.logo} alt="" className="w-6 h-6 rounded-full mr-3 flex-shrink-0" data-id="element-15238" data-mp="ewuq9ld3wl8e" /> : <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center mr-3 flex-shrink-0" data-id="element-15239" data-mp="2592g2sat7php">
                            <span className="text-xs font-medium text-gray-600" data-id="element-15240" data-mp="2a6u6fhvt33js">
                              {company.name.charAt(0)}
                            </span>
                          </div>}
                        <div className="min-w-0 flex-1" data-id="element-15241" data-mp="20b0vu41ndk4n">
                          <div className="text-sm font-medium text-gray-900 truncate" data-id="element-15242" data-mp="1maq4z10c85jh">
                            {company.name}
                          </div>
                        </div>
                      </div>
                      <div className="ml-2 flex-shrink-0" data-id="element-15243" data-mp="3gp93kshrdow8">
                        {isSelected && <CheckIcon size={16} className="text-blue-600" data-id="element-15244" data-mp="3llpq5i9b7v77" />}
                      </div>
                    </div>;
          })}
            </div>

            {/* Sub Merchants section */}
            <div data-id="element-15245" data-mp="1jpibxkxvhs27">
              <div className="px-3 py-2 bg-gray-50" data-id="element-15246" data-mp="1jq0ipbi5vood">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide" data-id="element-15247" data-mp="1jrlawuh06vj2">
                  Sub Merchants
                </span>
              </div>
              <div className="max-h-64 overflow-y-auto" data-id="element-15248" data-mp="1sovmyi1naop1">
                {companies.filter(c => c.type === 'submerchant').map(company => {
              const isSelected = selectedCompany.id === company.id;
              return <div key={company.id} role="option" aria-selected={isSelected} onClick={() => selectCompany(company)} className={`flex items-center px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`} data-id="element-15249" data-mp="1xqb7m8zwut6w">
                        <div className="flex items-center flex-1 min-w-0" data-id="element-15250" data-mp="x20yfz7nfmr3">
                          <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center mr-3 flex-shrink-0" data-id="element-15251" data-mp="120c746m7jr7q">
                            <BuildingIcon size={12} className="text-gray-500" data-id="element-15252" data-mp="17072amd0n2yl" />
                          </div>
                          <div className="min-w-0 flex-1" data-id="element-15253" data-mp="1bxqoq9jvam1m">
                            <div className="text-sm text-gray-900 truncate" data-id="element-15254" data-mp="1fzbwjghjoa4y">
                              {company.name}
                            </div>
                          </div>
                        </div>
                        <div className="ml-2 flex-shrink-0" data-id="element-15255" data-mp="1ppwvj514rgoe">
                          {isSelected && <CheckIcon size={16} className="text-blue-600" data-id="element-15256" data-mp="1upswevej6rzq" />}
                        </div>
                      </div>;
            })}
              </div>
            </div>
          </div>
        </>}
    </div>;
}